/**
 * 6.3 三种属性类型系统（借鉴 Appsmith）
 *
 * - Derived（派生）: 表达式从其他属性计算得出，运行时求值、不持久化
 *   `isValid: "{{ this.isRequired ? this.text && this.text.length > 0 : true }}"`
 * - Default（默认）: 属性面板默认值，作为节点 props 的兜底
 * - Meta（瞬态）: 不持久化但可用于绑定（如 isFocused），运行时状态
 *
 * 组件通过在 Widget 类上声明 getDefaultProperties / getDerivedProperties /
 * getMetaProperties 来暴露这些属性；运行时由 buildRuntimeProperties 合并求值，
 * 并作为 `{{ this.xxx }}` 绑定上下文注入绑定引擎。
 */
import type { BindingContext, BindingFunctions } from "./binding-engine";
import { evaluateExpression } from "./binding-engine";

export type PropertyKind = "derived" | "default" | "meta";

export interface DerivedPropertyDefinition {
  kind: "derived";
  name: string;
  /** 绑定表达式（可带 {{ }} 包裹），引用其它属性时使用 `this.xxx` */
  expression: string;
  label?: string;
  helpText?: string;
}

export interface DefaultPropertyDefinition {
  kind: "default";
  name: string;
  value: unknown;
}

export interface MetaPropertyDefinition {
  kind: "meta";
  name: string;
  initialValue: unknown;
  label?: string;
  helpText?: string;
}

export type PropertyDefinition =
  | DerivedPropertyDefinition
  | DefaultPropertyDefinition
  | MetaPropertyDefinition;

/** 按 kind 过滤属性定义 */
export function filterByKind(defs: PropertyDefinition[], kind: PropertyKind): PropertyDefinition[] {
  return defs.filter((d) => d.kind === kind);
}

/** 去除表达式外层 {{ }} 包裹（Appsmith 惯例） */
export function stripBindingWrappers(expression: string): string {
  const trimmed = expression.trim();
  const match = /^\{\{\s*([\s\S]*?)\s*\}\}$/.exec(trimmed);
  return match ? match[1] : trimmed;
}

/**
 * 合并构建 Widget 运行时属性包（this 上下文）：
 * 1. 默认值（default）垫底
 * 2. 节点 props（用户设置/已解析的绑定值）覆盖默认值
 * 3. 瞬态（meta）取 props 值或 initialValue
 * 4. 派生（derived）按声明顺序求值，后序派生可引用先序派生
 *
 * @param props 已通过 user/params/data 绑定解析的节点 props
 */
export function buildRuntimeProperties(
  defs: PropertyDefinition[],
  props: Record<string, unknown>,
  ctx: Omit<BindingContext, "this">,
  funcs: BindingFunctions,
): Record<string, unknown> {
  const bag: Record<string, unknown> = {};

  // 1. 默认值
  for (const def of filterByKind(defs, "default") as DefaultPropertyDefinition[]) {
    bag[def.name] = def.value;
  }

  // 2. props 覆盖（含 meta：props 有值则优先）
  for (const [key, value] of Object.entries(props)) {
    if (value !== undefined) bag[key] = value;
  }

  // 3. 未出现在 props 中的 meta 初始值
  for (const def of filterByKind(defs, "meta") as MetaPropertyDefinition[]) {
    if (!(def.name in bag)) {
      bag[def.name] = def.initialValue;
    }
  }

  // 4. 派生属性按声明顺序求值
  for (const def of filterByKind(defs, "derived") as DerivedPropertyDefinition[]) {
    try {
      const expression = stripBindingWrappers(def.expression);
      const value = evaluateExpression(expression, { ...ctx, this: bag }, funcs);
      if (value !== undefined) bag[def.name] = value;
    } catch {
      // 派生表达式求值失败时保持默认/undefined，不影响其它属性
      bag[def.name] = undefined;
    }
  }

  return bag;
}
